import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import instance from "../../../constants/config";
import { useSnackbar } from "notistack";
import { routes } from "../../../constants/routes";
import {
  clearCooldown,
  getCooldownRemainingSeconds,
  getVerifyCooldownKey,
  normalizeEmail,
  saveCooldownFromRetryAfter,
} from "../../../utils/otpCooldown";

interface LocationState {
  email: string;
  otpSessionId?: string;
  fromPasswordReset: boolean;
}

const ConfirmOTP: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const { email, otpSessionId: initialOtpSessionId } =
    (location.state as LocationState) || {};

  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [now, setNow] = useState(Date.now());
  const [otpSessionId, setOtpSessionId] = useState(initialOtpSessionId || "");

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const normalizedEmail = normalizeEmail(email || "");
  const verifyCooldownKey = normalizedEmail
    ? getVerifyCooldownKey(normalizedEmail, otpSessionId || "")
    : "";
  const verifyCooldownSeconds = verifyCooldownKey
    ? getCooldownRemainingSeconds(verifyCooldownKey, now)
    : 0;

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      enqueueSnackbar("No email provided. Please try again.", {
        variant: "error",
      });
      navigate(routes.LOGIN); // or wherever you want to redirect
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, navigate]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  const handleOtpChange = (index: number, value: string): void => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;

    setError(""); // Clear error when user starts typing

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const pastedOtp = pastedData.replace(/\D/g, "").slice(0, 6).split("");

    const newOtp = [...otp];
    pastedOtp.forEach((digit, index) => {
      if (index < 6) newOtp[index] = digit;
    });
    setOtp(newOtp);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtp.findIndex((digit) => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerifyOtp = async (): Promise<void> => {
    if (verifyCooldownSeconds > 0) {
      setError(`Too many OTP attempts. Try again in ${verifyCooldownSeconds}s.`);
      return;
    }

    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      setError("Please enter a complete 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const payload: Record<string, string> = {
        email,
        otp: otpCode,
      };
      if (otpSessionId) {
        payload.otpSessionId = otpSessionId;
      }

      const response = await instance.post(
        "/Auth/verify_forgot_password_otp",
        payload
      );

      if (verifyCooldownKey) {
        clearCooldown(verifyCooldownKey);
      }

      enqueueSnackbar(response.data.message || "OTP verified successfully!", {
        variant: "success",
      });

      // Navigate to create new password screen
      navigate("/create-new-password", {
        state: {
          email,
          otpVerified: true,
        },
      });
    } catch (error: any) {
      if (error.response?.status === 429 && verifyCooldownKey) {
        const retryAfterHeader = error.response?.headers?.["retry-after"];
        saveCooldownFromRetryAfter(verifyCooldownKey, retryAfterHeader, 60);
      }

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to verify OTP. Please try again.";

      setError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });

      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async (): Promise<void> => {
    setIsResending(true);
    setError("");

    try {
      const response = await instance.post("/Auth/forgot_password", { email });
      setOtpSessionId(
        response.data?.otpSessionId || response.data?.data?.otpSessionId || ""
      );

      enqueueSnackbar("New OTP has been sent to your email!", {
        variant: "success",
      });
      setOtp(["", "", "", "", "", ""]); // Clear current OTP
      inputRefs.current[0]?.focus();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to resend OTP. Please try again.";

      setError(errorMessage);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleGoBack = (): void => {
    navigate(-1); // Go back to previous screen
  };

  if (!email) {
    return null; // Will redirect in useEffect
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 12 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Verify OTP
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            We've sent a 6-digit verification code to
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="primary">
            {email}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box mb={3}>
          <Typography variant="body2" textAlign="center" mb={2}>
            Enter the 6-digit code
          </Typography>

          <Box display="flex" justifyContent="center" gap={1} mb={2}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(ref: HTMLInputElement | null) =>
                  (inputRefs.current[index] = ref)
                }
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                inputProps={{
                  maxLength: 1,
                  style: { textAlign: "center", fontSize: "1.5rem" },
                }}
                sx={{
                  width: 56,
                  "& .MuiOutlinedInput-root": {
                    height: 56,
                  },
                }}
                disabled={isLoading}
              />
            ))}
          </Box>
        </Box>

        <Box mb={3}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyOtp}
            disabled={
              isLoading || otp.join("").length !== 6 || verifyCooldownSeconds > 0
            }
            sx={{ mb: 2, height: 48 }}
            startIcon={
              isLoading && <CircularProgress size={20} color="inherit" />
            }
          >
            {isLoading
              ? "Verifying..."
              : verifyCooldownSeconds > 0
              ? `Try again in ${verifyCooldownSeconds}s`
              : "Verify OTP"}
          </Button>
          {verifyCooldownSeconds > 0 && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mb: 2, textAlign: "center" }}
            >
              Too many OTP attempts. Try again in {verifyCooldownSeconds}s.
            </Typography>
          )}

          <Button
            fullWidth
            variant="outlined"
            onClick={handleResendOtp}
            disabled={isResending}
            sx={{ mb: 2, height: 48 }}
            startIcon={
              isResending && <CircularProgress size={20} color="inherit" />
            }
          >
            {isResending ? "Resending..." : "Resend OTP"}
          </Button>

          <Button
            fullWidth
            variant="text"
            onClick={handleGoBack}
            disabled={isLoading}
            sx={{ height: 48 }}
          >
            Go Back
          </Button>
        </Box>

        <Typography variant="body2" textAlign="center" color="text.secondary">
          Didn't receive the code? Check your spam folder or try resending.
        </Typography>
      </Paper>
    </Container>
  );
};

export default ConfirmOTP;
